import { useEffect, useState } from 'react';
import { createClientQuiz, deleteClientQuiz, listClientQuizzes, markClientQuizApplied } from './api';
import type { ClientQuizList, QuizPart, QuizType } from './api';

/** Re-exported so UI code (CoverQuizDialog) reaches it through this module's own
 * manual test mock, rather than importing api.ts directly — api.ts's `import {
 * fetch } from '../../utils'` otherwise drags the real Redux store into a
 * component test that only meant to stub the ClientQuiz cache. */
export { QUIZ_LINK_PREFIX, quizLinkOf } from './api';

/**
 * One copy of each client's quiz state per factsheet+type, shared by every
 * reader (useCoverQuiz has six, useRecruitQuiz three — §15): one request while
 * any of them is mounted, refreshed by polling (5 s while a dialog is open,
 * §5.5) and after every change the adviser makes.
 */

export const CLIENT_QUIZ_CHANGED_EVENT = 'client-quiz-changed';
const STALE_MS = 60 * 1000;

type Entry = {
  list: ClientQuizList | null;
  loadedAt: number;
  inFlight: Promise<ClientQuizList | null> | null;
  /** Bumped per request; only the newest request's answer is kept. */
  seq: number;
  /** The last request failed — `list` (if any) is what was known before. */
  failed: boolean;
};
const entries = new Map<string, Entry>();
const keyOf = (factsheetId: string, type: QuizType) => `${factsheetId}:${type}`;
const entryOf = (key: string): Entry => {
  const existing = entries.get(key);
  if (existing) return existing;
  const created: Entry = { list: null, loadedAt: 0, inFlight: null, seq: 0, failed: false };
  entries.set(key, created);
  return created;
};
const announce = (key: string) => window.dispatchEvent(new CustomEvent(CLIENT_QUIZ_CHANGED_EVENT, { detail: { key } }));

export const readClientQuizzes = (factsheetId: string | null | undefined, type: QuizType): ClientQuizList | null =>
  factsheetId ? entries.get(keyOf(factsheetId, type))?.list || null : null;

/** Whether the last attempt to load this list failed (so what is shown may be out of date). */
export const readClientQuizzesFailed = (factsheetId: string | null | undefined, type: QuizType): boolean =>
  factsheetId ? Boolean(entries.get(keyOf(factsheetId, type))?.failed) : false;

/**
 * A poll joins a request already in flight. A refresh after the adviser's own
 * change (`fresh`) never does: a GET that left before the change would bring
 * back the list from before it (the new link missing, a withdrawn one still
 * there) — so it starts its own, and an older answer landing later is dropped.
 */
export const refreshClientQuizzes = (
  factsheetId: string,
  type: QuizType,
  options: { fresh?: boolean } = {}
): Promise<ClientQuizList | null> => {
  const key = keyOf(factsheetId, type);
  const entry = entryOf(key);
  if (entry.inFlight && !options.fresh) return entry.inFlight;
  entry.seq += 1;
  const seq = entry.seq;
  const request: Promise<ClientQuizList | null> = listClientQuizzes(factsheetId, type)
    .then(list => {
      if (seq !== entry.seq) return entry.list;
      entry.list = list;
      entry.loadedAt = Date.now();
      entry.failed = false;
      announce(key);
      return list;
    })
    .catch(() => {
      if (seq === entry.seq && !entry.failed) {
        entry.failed = true;
        announce(key);
      }
      return entry.list;
    })
    .finally(() => {
      if (entry.inFlight === request) entry.inFlight = null;
    });
  entry.inFlight = request;
  return request;
};

/**
 * One poll timer per factsheet+type, however many readers ask for it, running at
 * the shortest interval any of them wants (e.g. a dialog's 5 s over a card's none).
 */
const pollers = new Map<string, { wants: Map<object, number>; timer: number | null; ms: number }>();
const repoll = (key: string, factsheetId: string, type: QuizType) => {
  const poller = pollers.get(key);
  if (!poller) return;
  const ms = poller.wants.size ? Math.min(...poller.wants.values()) : 0;
  if (ms === poller.ms) return;
  if (poller.timer !== null) window.clearInterval(poller.timer);
  poller.ms = ms;
  poller.timer = ms ? window.setInterval(() => void refreshClientQuizzes(factsheetId, type), ms) : null;
  if (!ms) pollers.delete(key);
};
const wantPoll = (key: string, factsheetId: string, type: QuizType, reader: object, ms: number) => {
  const poller = pollers.get(key) || { wants: new Map(), timer: null, ms: 0 };
  pollers.set(key, poller);
  poller.wants.set(reader, ms);
  repoll(key, factsheetId, type);
  return () => {
    poller.wants.delete(reader);
    repoll(key, factsheetId, type);
  };
};

export const useClientQuizzes = (
  factsheetId: string | null | undefined,
  type: QuizType,
  options: { pollMs?: number; load?: boolean } = {}
): ClientQuizList | null => {
  const [, setVersion] = useState(0);
  // `load: false` reads what is cached without asking the server — for a
  // dialog that stays mounted while closed.
  const { pollMs, load = true } = options;
  useEffect(() => {
    if (!factsheetId) return undefined;
    const key = keyOf(factsheetId, type);
    const onChange = (event: Event) => {
      if ((event as CustomEvent<{ key: string }>).detail?.key === key) setVersion(v => v + 1);
    };
    window.addEventListener(CLIENT_QUIZ_CHANGED_EVENT, onChange);
    const entry = entryOf(key);
    if (load && (!entry.list || Date.now() - entry.loadedAt > STALE_MS)) void refreshClientQuizzes(factsheetId, type);
    const stopPolling = pollMs ? wantPoll(key, factsheetId, type, {}, pollMs) : null;
    return () => {
      window.removeEventListener(CLIENT_QUIZ_CHANGED_EVENT, onChange);
      if (stopPolling) stopPolling();
    };
  }, [factsheetId, type, pollMs, load]);
  return readClientQuizzes(factsheetId, type);
};

export const sendQuizInvitation = async (
  factsheetId: string,
  type: QuizType,
  part: QuizPart,
  displayName: string,
  locale?: string
) => {
  const quiz = await createClientQuiz({ factsheetId, type, part, displayName, ...(locale ? { locale } : {}) });
  await refreshClientQuizzes(factsheetId, type, { fresh: true });
  return quiz;
};

/** Resolves false when the row was no longer in the state the dialog showed (404) — the list is refreshed either way. */
export const removeQuiz = async (factsheetId: string, type: QuizType, id: string, status: 'sent' | 'completed') => {
  try {
    await deleteClientQuiz(id, status);
    return true;
  } catch (error) {
    return false;
  } finally {
    await refreshClientQuizzes(factsheetId, type, { fresh: true });
  }
};

export const stampQuizApplied = async (factsheetId: string, type: QuizType, id: string, disc = false) => {
  try {
    await markClientQuizApplied(id, disc);
  } finally {
    await refreshClientQuizzes(factsheetId, type, { fresh: true });
  }
};

export const __resetClientQuizCacheForTests = () => {
  entries.clear();
  pollers.forEach(poller => poller.timer !== null && window.clearInterval(poller.timer));
  pollers.clear();
};
